import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  HttpStatusCode,
  InternalAxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios';
import { get, isObject, isUndefined } from 'lodash';

import { CommonResponse } from 'src/model/common';
import { ACCESS_TOKEN_KEY, DEFAULT_IP_ADDRESS, IP_ADDRESS_KEY } from 'src/utils/constants/config';
import { getDeviceId } from './auth';
import { ERROR_CODE_MESSAGE } from 'src/utils/constants/statusCode';

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 1000 * 60,
  headers: {},
});

instance.interceptors.request.use(
  function (config: InternalAxiosRequestConfig<ExpectedAny>) {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const ipAddress = localStorage.getItem(IP_ADDRESS_KEY);
    const deviceId = getDeviceId();

    config.headers['Ip-Address'] = ipAddress || DEFAULT_IP_ADDRESS;
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    config.headers['Device-Id'] = deviceId;
    if (accessToken) {
      config.headers['Authorization'] = 'Bearer ' + accessToken;
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  (axiosResponse: AxiosResponse) => {
    if (axiosResponse.status >= 500) {
      alert('Server Error!');
    }

    // if (axiosResponse.status === ERROR_CODE.FORBIDDEN) {
    //   handleError();
    //   return axiosResponse;
    // }

    // const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    // if (refreshToken && axiosResponse.status === ERROR_CODE.UNAUTHORIZED) {
    //   AuthService.handleRefreshToken({ refresh_token: refreshToken || '' })
    //     .then((response) => {
    //       if (response.status_code === ERROR_CODE.UNAUTHORIZED || response.status_code === 0) {
    //         handleError();
    //         // return instance(axiosResponse.config);
    //         return axiosResponse;
    //       }
    //       const newAccessToken = response.data;
    //       localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
    //       return instance(axiosResponse.config);
    //     })
    //     .catch(() => {
    //       handleError();
    //       // return instance(axiosResponse.config);
    //       return axiosResponse;
    //     });
    // }
    return axiosResponse;
  },
  function (error: AxiosError) {
    return Promise.reject(error);
  },
);

// instance.defaults.validateStatus = (status) => (status >= 200 && status < 400) || status === ERROR_CODE.UNAUTHORIZED;

class HTTP {
  instance: AxiosInstance;
  constructor() {
    this.instance = instance;
  }
}

export const http = new HTTP().instance;

export const postRequest = (
  url: string,
  body: ExpectedAny,
  headers?: RawAxiosRequestHeaders,
  defaultResult = new CommonResponse(),
) => {
  return http
    .post(url, body, {
      headers: headers,
    })
    .then((axiosResponse: AxiosResponse) => {
      const data = axiosResponse.data?.data;
      return { ...axiosResponse.data, ...{ data: data } };
    })
    .catch((error) => {
      const data = error?.response?.data;
      return { ...defaultResult, ...{ data: data, status_code: error.status } };
    });
};

export const deleteRequest = (
  url: string,
  dto?: any,
  headers?: AxiosRequestHeaders,
  defaultResult = new CommonResponse(),
) => {
  return http
    .delete(url, {
      headers: headers,
      data: dto,
    })
    .then((axiosResponse: AxiosResponse) => {
      const data = axiosResponse.data?.data;
      return { ...axiosResponse.data, ...{ data: data } };
    })
    .catch(() => {
      return defaultResult;
    });
};

export const putRequest = (
  url: string,
  body: ExpectedAny,
  headers?: AxiosRequestHeaders,
  defaultResult = new CommonResponse(),
) => {
  return http
    .put(url, body, {
      headers: headers,
    })
    .then((axiosResponse: AxiosResponse) => {
      const data = axiosResponse.data?.data ?? [];
      return { ...axiosResponse.data, ...{ data: data } };
    })
    .catch(() => {
      return defaultResult;
    });
};

export const getRequest = (
  url: string,
  params: ExpectedAny,
  defaultResult = new CommonResponse(),
  headers?: RawAxiosRequestHeaders,
) => {
  return http
    .get(url, {
      params: params,
      headers: headers,
      paramsSerializer: {
        indexes: null, // Why null? -> Docs: https://github.com/axios/axios/tree/v1.7.8#:~:text=in%20the%20params.-,indexes,-%3A%20false%20//%20Three
      },
    })
    .then((axiosResponse: AxiosResponse) => {
      const defaultData = get(defaultResult, 'data', {});
      const responseData = !isUndefined(axiosResponse?.data?.data)
        ? get(axiosResponse, 'data.data', null)
        : get(axiosResponse, 'data', null);
      return { ...axiosResponse.data, data: responseData || defaultData, httpStatus: axiosResponse.status };
    })
    .catch(async (error) => {
      if (
        error?.response?.data?.status_code === HttpStatusCode.BadRequest &&
        [
          ERROR_CODE_MESSAGE.FEATURE_NOT_FOUND,
          ERROR_CODE_MESSAGE.FEATURE_TYPE_NOT_FOUND,
          ERROR_CODE_MESSAGE.ENDPOINT_NOT_FOUND,
        ].includes(error?.response?.data?.error?.code || '')
      ) {
        if (error?.response?.data instanceof Blob && error?.response?.data.type === 'application/json') {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          error.response.data = errorJson;
        }
        throw error;
      } else {
        const errorData =
          !isUndefined(error?.response?.data) && isObject(error?.response?.data)
            ? get(error, 'response.data', {})
            : get(defaultResult, 'data', {});
        return { ...defaultResult, ...{ data: errorData, status_code: error.status } };
      }
    });
};

export const getBlobRequest = (url: string, params: ExpectedAny, headers?: RawAxiosRequestHeaders) => {
  return http
    .get(process.env.REACT_APP_API_URL + url, {
      params: params,
      responseType: 'blob',
      headers: headers,
    })
    .then((axiosResponse: AxiosResponse) => {
      return axiosResponse;
    })
    .catch(async (error) => {
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        const errorText = await error.response.data.text();
        const errorJson = JSON.parse(errorText);
        error.response.data = errorJson;
      }
      throw error;
    });
};

export const postRequestBlob = (
  url: string,
  body: ExpectedAny,
  headers?: AxiosRequestHeaders,
  defaultResult = new CommonResponse(),
) => {
  return http
    .post(url, body, {
      headers: headers,
      responseType: 'blob',
    })
    .then((axiosResponse: AxiosResponse) => {
      return axiosResponse.data;
    })
    .catch((error: AxiosError) => {
      return defaultResult;
    });
};
